import { useState } from 'react'
import { useLocation, useOutletContext } from 'react-router-dom'

import { Box, Grid } from '@factorialco/deprecated-design-system'
import { MyProjectsOverviewDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import EmployeeViewCostHistory from 'modules/project_management/components/EmployeeViewCostHistory'
import { InsightCard } from 'modules/project_management/components/InsightCard'
import PendingTasksSidePanel from 'modules/project_management/components/PendingTasksSidePanel'
import EmployeeViewDistributionChart from 'modules/project_management/components/charts/EmployeeViewDistributionChart'
import ProjectsAssignedDatacollection from 'modules/project_management/components/tables/ProjectsAssignedDatacollection'
import { RouteContextParams } from 'modules/project_management/pages/MyProjectsRootPage'
import { TaskAnalytics } from 'modules/tasks/components/TasksList/components/TaskAnalytics'

import { overviewVariables } from './overviewData'

const MyProjectsOverviewPage = () => {
  useEffect(() => {
    track(trackingEvents.projectManagement.flexibleTracking.overview.visitMyProjectsOverviewSection)
  }, [])

  const [showPendingTasksSidePanel, setShowPendingTasksSidePanel] = useState<boolean>(false)
  const { pathname } = useLocation()
  const { company } = useCurrentSession()
  const { accessId, employeeIdNumber, employeeName, hasProjectCosts } =
    useOutletContext<RouteContextParams>()

  const { data, loading } = useQuery(MyProjectsOverviewDocument, {
    variables: {
      ...overviewVariables(accessId, employeeIdNumber, hasProjectCosts),
    },
  })

  const projectsAssigned = data?.projectManagement.projectsAssigned.totalCount || 0
  const projectWorkerIds =
    data?.projectManagement.projectsAssigned.nodes.map((node) => node.id) || []
  const employeeInputedMinutes =
    data?.projectManagement.projectsAssigned.nodes.reduce(
      (acc, node) => acc + (node.inputedMinutes ?? 0),
      0
    ) || 0

  const companyCurrencyEmployeeLaborCostCents =
    data?.projectManagement.projectsAssigned.nodes.reduce(
      (acc, node) => acc + (node.companyLaborCostCents ?? 0),
      0
    ) || 0

  const totalDueInfuture = data?.tasks.dueInFuture.totalCount || 0
  const totalOverDue = data?.tasks.overdue.totalCount || 0
  const noDueDateTotal = data?.tasks.noDueDate.totalCount || 0
  const toDoTasks = data?.tasks.toDo.totalCount || 0
  const inProgressTasks = data?.tasks.inProgress.totalCount || 0
  const pendingTasks = toDoTasks + inProgressTasks

  return (
    <Box gap="s24">
      <Grid variant="fixed">
        <Box gridColSpan={{ xxs: 12, s: 4 }}>
          {loading ? (
            <InsightCard.Skeleton
              title={i18n.t(
                'project_management.my_projects.overview.insight_cards.projects_assigned'
              )}
            />
          ) : (
            <InsightCard.Counter
              title={i18n.t(
                'project_management.my_projects.overview.insight_cards.projects_assigned'
              )}
              totalCount={projectsAssigned}
            />
          )}
        </Box>
        <Box gridColSpan={{ xxs: 12, s: 4 }}>
          {loading ? (
            <InsightCard.Skeleton
              title={i18n.t('project_management.my_projects.overview.insight_cards.pending_tasks')}
              withStackedBar
            />
          ) : (
            <TaskAnalytics
              data={{
                total: pendingTasks,
                totalDueInfuture,
                totalOverDue,
                noDueDateTotal,
              }}
              title={i18n.t('project_management.my_projects.overview.insight_cards.pending_tasks')}
              touchable
              touchingAction={() => {
                setShowPendingTasksSidePanel(true)
                track(
                  trackingEvents.projectManagement.flexibleTracking.overview
                    .openProjectsPendingTasksSidePanel
                )
              }}
            />
          )}
        </Box>
      </Grid>
      <ProjectsAssignedDatacollection accessId={accessId} employeeId={employeeIdNumber} />
      <EmployeeViewDistributionChart employeeId={employeeIdNumber} employeeName={employeeName} />
      {pathname.includes('contributors') && companyCurrencyEmployeeLaborCostCents > 0 && (
        <Box paddingY="s16">
          <EmployeeViewCostHistory
            employeeInputedMinutes={employeeInputedMinutes}
            employeeLaborCostCents={companyCurrencyEmployeeLaborCostCents}
            employeeLegalEntityCurrency={company.currency || 'EUR'}
            projectWorkerIds={projectWorkerIds}
          />
        </Box>
      )}
      <PendingTasksSidePanel
        assigneeId={accessId}
        onClose={() => setShowPendingTasksSidePanel(false)}
        showPendingTasksSidePanel={showPendingTasksSidePanel}
      />
    </Box>
  )
}
export default MyProjectsOverviewPage
